import { Route, Switch, useLocation } from "wouter";
import { useEffect } from "react";
import { useMe } from "./lib/auth";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AcceptInvite from "./pages/AcceptInvite";
import { DashboardShell } from "./components/DashboardShell";
import { SectionPlaceholder } from "./pages/Section";
import Overview from "./pages/Overview";
import Conversations from "./pages/Conversations";
import Leads from "./pages/Leads";
import Bookings from "./pages/Bookings";
import Handoffs from "./pages/Handoffs";
import Callbacks from "./pages/Callbacks";
import Integrations from "./pages/Integrations";
import Settings from "./pages/Settings";
import Templates from "./pages/Templates";
import Billing from "./pages/Billing";
import VoiceTest from "./pages/VoiceTest";

const PUBLIC_PATHS = ["/login", "/forgot-password", "/reset", "/accept-invite"];

function isPublicPath(p: string): boolean {
  return PUBLIC_PATHS.some((pub) => p === pub || p.startsWith(`${pub}?`));
}

export default function App() {
  const [location, setLocation] = useLocation();
  const me = useMe();

  // Auth gate. Public routes (login + the three token-bearing flows) are
  // reachable without a session; everything else redirects to /login.
  useEffect(() => {
    if (me.isLoading) return;
    const isPublic = isPublicPath(location);
    if (!me.data && !isPublic) setLocation("/login");
    if (me.data && location === "/login") setLocation("/overview");
    if (me.data && location === "/") setLocation("/overview");
  }, [me.isLoading, me.data, location, setLocation]);

  if (me.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">
        Loading…
      </div>
    );
  }

  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset" component={ResetPassword} />
      <Route path="/accept-invite" component={AcceptInvite} />
      {me.data && (
        <Route>
          <DashboardShell me={me.data} activeSection={sectionFromPath(location)}>
            <Switch>
              <Route path="/overview" component={Overview} />
              <Route path="/conversations" component={Conversations} />
              <Route path="/leads" component={Leads} />
              <Route path="/bookings" component={Bookings} />
              <Route path="/handoffs" component={Handoffs} />
              <Route path="/callbacks" component={Callbacks} />
              <Route path="/billing" component={Billing} />
              <Route path="/voice-test" component={VoiceTest} />
              <Route path="/integrations" component={Integrations} />
              <Route path="/templates" component={Templates} />
              <Route path="/settings" component={Settings} />
              <Route>
                <SectionPlaceholder
                  title="Page not found"
                  hint="Pick a section from the sidebar."
                />
              </Route>
            </Switch>
          </DashboardShell>
        </Route>
      )}
    </Switch>
  );
}

function sectionFromPath(p: string): string {
  // /overview → "overview", /conversations → "conversations", etc.
  const m = p.match(/^\/([^/]+)/);
  return m ? m[1] : "overview";
}
